import {
  Shield,
  BadgeCheck,
  Banknote,
  CloudRain,
  Layers,
  FileText,
  Clock,
} from "lucide-react";
import { AreaPageShell } from "./AreaPageShell";

export interface StormDamageFaq {
  question: string;
  answer: string;
}

export interface StormDamagePageData {
  city: string;
  citySlug: string;
  serviceName: string;
  title: string;
  description: string;
  h1: string;
  heroIntro: string;
  climateText: string;
  localPresenceText: string;
  warrantyText: string;
  pricingText: string;
  priceFactorsText: string;
  financingText: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  matchText: string;
  upgradeText: string;
  metalText: string;
  faqs: StormDamageFaq[];
}

const buildSchema = (data: StormDamagePageData) => {
  const base = "https://www.saskroofingcompany.com";
  const url = `${base}/areas/${data.citySlug}/storm-damage`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${base}/#localbusiness`,
    name: "Sask Roofing Company",
    url: `${base}/`,
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: data.city,
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    serviceType: "Storm Damage Roof Repair",
    name: "Storm Damage Roof Repair",
    description: data.description,
    url,
    provider: {
      "@id": `${base}/#localbusiness`,
      "@type": "RoofingContractor",
      name: "Sask Roofing Company",
      url: `${base}/`,
      telephone: "+1-639-739-7969",
    },
    areaServed: { "@type": "City", name: data.city },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "CAD",
      lowPrice: "500",
      highPrice: "15000",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `${base}/contact`,
      servicePhone: {
        "@type": "ContactPoint",
        telephone: "+1-639-739-7969",
        contactType: "customer service",
        areaServed: "CA-SK",
        availableLanguage: "English",
      },
    },
    termsOfService: `${base}/about#warranty`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "87",
      bestRating: "5",
      worstRating: "1",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${base}/areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.city,
        item: `${base}/areas/${data.citySlug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Storm Damage",
        item: url,
      },
    ],
  };

  return [breadcrumbSchema, localBusinessSchema, serviceSchema, faqSchema];
};

export function StormDamagePage({ data }: { data: StormDamagePageData }) {
  return (
    <AreaPageShell
      data={{
        ...data,
        schema: buildSchema(data),
        canonical: `/areas/${data.citySlug}/storm-damage`,
      }}
      heroImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/67bfca73-3cfe-4fd0-b9fd-9ed4f4780799.png&output=webp&q=60"
      heroAlt={`Storm damage roof repair in ${data.city}`}
      whyBlocks={[
        {
          icon: CloudRain,
          title: `Saskatchewan's hail corridor & storm damage in ${data.city}`,
          text: data.climateText,
        },
        {
          icon: Shield,
          title: `Local storm damage repair crews in ${data.city}`,
          text: data.localPresenceText,
        },
        {
          icon: BadgeCheck,
          title: `Storm damage repair warranties in ${data.city}`,
          text: data.warrantyText,
        },
      ]}
      costBlocks={[
        {
          title: `Storm damage repair price range in ${data.city}`,
          text: data.pricingText,
        },
        {
          title: `What affects storm damage cost in ${data.city}`,
          text: data.priceFactorsText,
        },
        {
          title: `Storm damage financing in ${data.city}`,
          text: data.financingText,
        },
      ]}
      extraCostTiles={[
        {
          label: "Minor repair",
          value: "$500–$1,500",
          note: "few hail-bruised shingles",
        },
        {
          label: "Larger repair",
          value: "$3K–$8K",
          note: "multiple slopes / decking",
        },
        {
          label: "Full replacement",
          value: "$6K–$15K+",
          note: "often insurance-covered",
        },
      ]}
      extraCallout={{
        icon: FileText,
        title: `Insurance claim documentation in ${data.city}`,
        text: "Most significant storm damage is covered by homeowner's insurance minus your deductible. We photograph and document every bit of damage so your adjuster has what they need — and we meet them on-site to walk through the findings together.",
      }}
      processSteps={[
        {
          title: `Emergency tarp & storm damage assessment in ${data.city}`,
          text: data.step1,
        },
        {
          title: `Insurance docs & adjuster coordination in ${data.city}`,
          text: data.step2,
        },
        {
          title: `Storm damage material selection & scheduling`,
          text: data.step3,
        },
        {
          title: `Storm damage repair & final walkthrough in ${data.city}`,
          text: data.step4,
        },
      ]}
      materialBlocks={[
        {
          icon: Layers,
          title: `Matching your existing roof in ${data.city}`,
          text: data.matchText,
        },
        {
          icon: CloudRain,
          title: `Hail-rated architectural shingles in ${data.city}`,
          text: data.upgradeText,
        },
        {
          icon: Shield,
          title: `Metal roofing for severe weather in ${data.city}`,
          text: data.metalText,
        },
      ]}
    />
  );
}
